import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, GripVertical, ListTodo } from 'lucide-react';
import { usePulseWorkbenchContext } from '@/contexts/pulse-workbench-context';
import { EditableStatus } from '@/components/editable-status';
import { StatusBadge } from '@/components/status-badge';
import { useStatusConfigs } from '@/hooks/use-status-configs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import type { Task } from '@shared/schema';

interface WorkbenchTasksPanelProps {
  tasks: Task[];
  sequences: Array<{ sequenceNumber: string; sequenceName: string }>;
  isLoading: boolean;
}

export function WorkbenchTasksPanel({ tasks, sequences, isLoading }: WorkbenchTasksPanelProps) {
  const { drafts, addDraft, removeDraft, updateDraft, activePulseId } = usePulseWorkbenchContext();
  const [newTaskText, setNewTaskText] = useState('');
  const queryClient = useQueryClient();
  const { statusConfigs: taskStatusConfigsRaw } = useStatusConfigs('task');
  
  const taskStatusConfigs = Array.isArray(taskStatusConfigsRaw) 
    ? [...taskStatusConfigsRaw].sort((a, b) => a.sortOrder - b.sortOrder)
    : [];

  const updateTaskMutation = useMutation({
    mutationFn: async ({ taskId, updates }: { taskId: number; updates: Partial<Task> }) => {
      return apiRequest('PATCH', `/api/tasks/${taskId}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/pulse-workbench', activePulseId] });
      queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'] });
    },
  });

  const handleStatusChange = (taskId: number, newStatus: string) => {
    updateTaskMutation.mutate({ taskId, updates: { status: newStatus } });
  };

  const handleCheckboxToggle = (task: Task) => {
    const isDone = task.status === 'done' || task.status === 'Done';
    const newStatus = isDone ? 'working_on_it' : 'done';
    updateTaskMutation.mutate({ taskId: task.id, updates: { status: newStatus } });
  };

  const handleAddDraft = () => {
    if (newTaskText.trim()) {
      addDraft(newTaskText.trim());
      setNewTaskText('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddDraft();
    }
  };

  return (
    <div className="h-full flex flex-col gap-4">
      <div className="flex gap-2">
        <Input
          value={newTaskText}
          onChange={(e) => setNewTaskText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a new task..."
          className="flex-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          data-testid="input-new-task"
        />
        <Button
          onClick={handleAddDraft}
          disabled={!newTaskText.trim()}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="button-add-draft"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add to Draft
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="space-y-2">
          {drafts.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-slate-400 mb-2 flex items-center gap-2">
                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
                  DRAFTS
                </span>
                <span className="text-xs text-slate-500">
                  (not saved yet)
                </span>
              </h3>
              <div className="space-y-1">
                {drafts.map((draft) => (
                  <div
                    key={draft.id}
                    className="flex items-center gap-2 p-2 bg-blue-500/10 border border-blue-500/30 rounded-lg group"
                    data-testid={`draft-task-${draft.id}`}
                  >
                    <GripVertical className="h-4 w-4 text-slate-600 cursor-grab" />
                    <Input
                      value={draft.text}
                      onChange={(e) => updateDraft(draft.id, { text: e.target.value })}
                      className="flex-1 bg-transparent border-none text-white text-sm h-8 focus-visible:ring-0"
                      data-testid={`input-draft-${draft.id}`}
                    />
                    <Select
                      value={draft.status || 'to do'}
                      onValueChange={(value) => updateDraft(draft.id, { status: value })}
                    >
                      <SelectTrigger 
                        className="w-auto min-w-[100px] h-7 bg-transparent border-slate-600 text-xs"
                        data-testid={`select-draft-status-${draft.id}`}
                      >
                        <SelectValue>
                          <StatusBadge status={draft.status || 'to do'} type="task" />
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-600">
                        {taskStatusConfigs.map((config) => (
                          <SelectItem 
                            key={config.statusKey} 
                            value={config.statusKey}
                            className="text-slate-300 hover:bg-slate-700"
                          >
                            <StatusBadge status={config.statusKey} type="task" />
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeDraft(draft.id)}
                      className="h-7 w-7 text-slate-400 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`button-remove-draft-${draft.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tasks.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-slate-400 mb-2">
                Existing Tasks ({tasks.length})
              </h3>
              <div className="space-y-1">
                {tasks.map((task) => {
                  const isDone = task.status === 'done' || task.status === 'Done';
                  
                  return (
                    <div
                      key={task.id}
                      className="flex items-center gap-3 p-2 bg-slate-800/50 border border-slate-700 rounded-lg hover:bg-slate-800/80 transition-colors"
                      data-testid={`task-${task.id}`}
                    >
                      <Checkbox
                        checked={isDone}
                        onCheckedChange={() => handleCheckboxToggle(task)}
                        className="h-4 w-4 border-slate-500 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                        data-testid={`checkbox-task-${task.id}`}
                      />
                      <span className={`flex-1 text-sm text-white ${isDone ? 'line-through opacity-60' : ''}`}>
                        {task.taskDescription}
                      </span>
                      <EditableStatus
                        status={task.status}
                        type="task"
                        onStatusChange={(newStatus) => handleStatusChange(task.id, newStatus)}
                        className="text-xs"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!isLoading && tasks.length === 0 && drafts.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <ListTodo className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No tasks yet</p>
              <p className="text-xs mt-1">Add a task above to get started</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
